import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { EVENT_TYPES, type AuditLogEntryDto } from "@platform/core";
import { DataGrid, EMPTY_FILTER_MODEL, type GridColDef, type GridFilterModel } from "@platform/ui";
import type { ApiClient } from "../api/client";
import { useApi, useConsole } from "../context";
import { useT } from "../i18n";
import { toCsv } from "../lib/csv";
import { formatDateTime } from "../lib/format";
import { PAGE_SIZES, useCursorPagination, useGridText } from "../lib/grid";
import { ErrorBanner, PageCard, ReloadButton } from "../layout/Page";

export interface AuditFilter {
  type?: string[];
  actor?: string;
  subject?: string;
  from?: string;
  to?: string;
}

/** Fetch every page for a filter (bounded) — used by CSV export. */
export async function fetchAllLogs(api: ApiClient, f: AuditFilter, max = 5000): Promise<AuditLogEntryDto[]> {
  const out: AuditLogEntryDto[] = [];
  let cursor: string | undefined;
  do {
    const page = await api.logs.list({ ...f, limit: 100, ...(cursor ? { cursor } : {}) });
    out.push(...page.items);
    cursor = page.nextCursor;
  } while (cursor && out.length < max);
  return out;
}

export function auditCsv(rows: AuditLogEntryDto[]): string {
  return toCsv(
    ["occurredAt", "type", "actorType", "actorId", "subjectUserId", "ip", "userAgent", "data"],
    rows.map((r) => [
      r.occurredAt,
      r.type,
      r.actor.type,
      r.actor.id,
      r.subjectUserId ?? "",
      r.actor.ip ?? "",
      r.actor.userAgent ?? "",
      r.data,
    ]),
  );
}

const toIso = (local: unknown) => {
  const d = new Date(String(local));
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

/** Grid filters → audit-log query (the API filters by type list, actor, subject and a time range). */
export function auditFilterOf(model: GridFilterModel): AuditFilter {
  const f: AuditFilter = {};
  for (const i of model.items) {
    const v = i.value;
    if (v === undefined || v === "" || (Array.isArray(v) && !v.length)) continue;
    if (i.field === "type") f.type = (Array.isArray(v) ? v : [v]).map(String);
    if (i.field === "actor") f.actor = String(v).trim();
    if (i.field === "subject") f.subject = String(v).trim();
    if (i.field === "occurredAt") {
      const iso = toIso(v);
      if (iso && i.operator === "onOrAfter") f.from = iso;
      if (iso && i.operator === "before") f.to = iso;
    }
  }
  return f;
}

export function AuditTable({ subject, compact }: { subject?: string; compact?: boolean }) {
  const { t, locale } = useT();
  const api = useApi();
  const { config } = useConsole();
  const text = useGridText();
  const gridId = compact ? "user-activity" : "audit-logs";
  const [filterModel, setFilterModel] = useState<GridFilterModel>(EMPTY_FILTER_MODEL);
  const f: AuditFilter = { ...auditFilterOf(filterModel), ...(subject ? { subject } : {}) };
  const paging = useCursorPagination(gridId, JSON.stringify(f));
  const q = useQuery({
    queryKey: ["logs", f, paging.model.pageSize, paging.cursor],
    queryFn: () =>
      api.logs.list({
        ...f,
        limit: paging.model.pageSize,
        ...(paging.cursor ? { cursor: paging.cursor } : {}),
      }),
    placeholderData: keepPreviousData,
  });

  const columns = useMemo<GridColDef<AuditLogEntryDto>[]>(
    () => [
      {
        field: "occurredAt",
        headerName: t("logs.col.time"),
        type: "dateTime",
        width: 180,
        sortable: false,
        filterOperators: ["onOrAfter", "before"],
        valueFormatter: (v) => formatDateTime(String(v), locale, config.timeZone),
        renderCell: ({ row, formatted }) => <time dateTime={row.occurredAt}>{formatted}</time>,
      },
      {
        field: "type",
        headerName: t("logs.col.event"),
        type: "singleSelect",
        minWidth: 200,
        sortable: false,
        filterOperators: ["is", "isAnyOf"],
        valueOptions: EVENT_TYPES.map((e) => ({ value: e, label: e })),
        renderCell: ({ row }) => (
          <span className="rounded-md bg-subtle px-1.5 py-0.5 font-mono text-xs ring-1 ring-line ring-inset">
            {row.type}
          </span>
        ),
      },
      {
        field: "actor",
        headerName: t("logs.col.actor"),
        minWidth: 180,
        sortable: false,
        filterOperators: ["equals"],
        valueGetter: (r) => r.actor.id,
        cellClassName: "truncate font-mono text-xs",
      },
      ...(subject
        ? []
        : [
            {
              field: "subject",
              headerName: t("logs.col.subject"),
              minWidth: 180,
              sortable: false,
              filterOperators: ["equals"],
              valueGetter: (r: AuditLogEntryDto) => r.subjectUserId,
              cellClassName: "truncate font-mono text-xs",
            } satisfies GridColDef<AuditLogEntryDto>,
          ]),
      {
        field: "ip",
        headerName: t("logs.col.ip"),
        width: 140,
        sortable: false,
        filterable: false,
        valueGetter: (r) => r.actor.ip,
        cellClassName: "truncate text-xs",
      },
      {
        field: "userAgent",
        headerName: "User agent",
        minWidth: 200,
        sortable: false,
        filterable: false,
        hiddenByDefault: true,
        valueGetter: (r) => r.actor.userAgent,
        cellClassName: "text-xs",
      },
    ],
    [t, locale, config.timeZone, subject],
  );

  return (
    <div>
      {q.isError && <ErrorBanner error={q.error} onRetry={() => void q.refetch()} />}
      <DataGrid
        id={gridId}
        label={t("logs.table")}
        localeText={text}
        mode="server"
        rows={q.data?.items ?? []}
        columns={columns}
        loading={q.isFetching}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        paginationModel={paging.model}
        onPaginationModelChange={(m) => paging.onChange(m, q.data?.nextCursor)}
        pageSizeOptions={PAGE_SIZES}
        hasNextPage={Boolean(q.data?.nextCursor)}
        disableFilterOr
        disableLastPage
        disableQuickFilter
        getRowAttributes={(r) => ({ "data-testid": "audit-row", "data-type": r.type })}
        getDetailPanelContent={(r) => (
          <pre className="overflow-x-auto font-mono text-xs whitespace-pre-wrap">
            {JSON.stringify({ actor: r.actor, subjectUserId: r.subjectUserId, data: r.data }, null, 2)}
          </pre>
        )}
        onExport={() => fetchAllLogs(api, f)}
        toCsv={auditCsv}
        exportFileName={`audit-logs-${new Date().toISOString().slice(0, 10)}`}
        noRowsContent={<p className="text-sm text-ink-2">{t("logs.empty")}</p>}
        toolbarActions={<ReloadButton onClick={() => void q.refetch()} />}
      />
    </div>
  );
}

export function AuditLogsPage() {
  return (
    <PageCard>
      <AuditTable />
    </PageCard>
  );
}
